import { type ComponentProps, useCallback, useMemo } from "react";

import { Breadcrumbs } from "@features/navigations/breadcrumbs";

import { COLORS_VALUES } from "@shared/constants";
import { ButtonWithIcon } from "@shared/ui/button-with-icon";
import { Image } from "@shared/ui/image";
import { Body, Heading } from "@shared/ui/typography";
import { parseLinkParams } from "@shared/utils";
import {
  formatDate,
  formatNumberWithSpaces,
  formatTimeToHM,
  isDifferentDate,
} from "@shared/utils/string";

import type { EventType } from "../../model";

import s from "./hero.module.scss";

export type HeroProps = ComponentProps<"div"> & {
  data: EventType;
};

export const Hero = (props: HeroProps) => {
  const { data } = props;

  const isDifferentYear = useMemo(() => {
    if (!data.date || !data.dateEnd) return false;
    return isDifferentDate(data.date, data.dateEnd, "year");
  }, [data.date, data.dateEnd]);

  const isDifferentMonth = useMemo(() => {
    if (!data.dateEnd) return true;
    return isDifferentDate(data.date, data.dateEnd, "month");
  }, [data.date, data.dateEnd]);

  const handleBuy = useCallback(() => {
    if (!data.widgetLink) {
      console.warn("[Event] Не передана ссылка для покупки билета");
      return;
    }

    if (!window.WPOpenModal) {
      console.error("[Event] WPOpenModal не подключен");
      return;
    }

    const params = parseLinkParams(data.widgetLink);

    window.WPOpenModal(params);
  }, [data.widgetLink]);

  const backgroundColor = data?.mainColor
    ? COLORS_VALUES[data.mainColor as keyof typeof COLORS_VALUES]
    : undefined;

  return (
    <section
      className={s.root}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      <div className={s.content}>
        <div className={s.leftTop}>
          <Breadcrumbs className={s.breadcrumbs} />
          <div className={s.info}>
            <Heading level={5} tag="p">
              {data.date && (
                <span>
                  {formatDate(data.date, isDifferentMonth, isDifferentYear)}
                </span>
              )}
              {data.dateEnd && (
                <span>
                  {" "}
                  - {formatDate(data.dateEnd, true, isDifferentYear)}
                </span>
              )}
              {!data.dateEnd && data.time && (
                <span>, {formatTimeToHM(data.time)}</span>
              )}
            </Heading>
            {data.location && (
              <Heading level={5} tag="p">
                {data.location}
              </Heading>
            )}
          </div>
        </div>
        <div className={s.leftBottom}>
          {data?.title && (
            <Heading level={3} tag="h1" className={s.title}>
              {data.title}
            </Heading>
          )}
          <div className={s.bottom}>
            <div className={s.buttons}>
              {/* {!!data.price && data.price > 0 && (
              <ButtonWithIcon
                onClick={handleBuy}
                iconName={data?.pageIcon?.icon?.name || "plus"}
                iconFillColor={data?.pageIcon?.fillColor || "gold"}
                iconStrokeColor={data?.pageIcon?.strokeColor || "bistre"}
                iconSvgCode={data?.pageIcon?.uicon?.svg_code}
                variant="secondary-light"
                className={s.button}
              >
                купить билет
              </ButtonWithIcon>
            )} */}
              {data.programDocuments && (
                <ButtonWithIcon
                  iconName={data?.pageIcon?.icon?.name || "plus"}
                  iconFillColor={data?.pageIcon?.fillColor || "gold"}
                  iconStrokeColor={data?.pageIcon?.strokeColor || "bistre"}
                  iconSvgCode={data?.pageIcon?.uicon?.svg_code}
                  variant="primary-light"
                  className={s.button}
                  href={data?.programDocuments?.url}
                  target="_blank"
                >
                  Скачать программу
                </ButtonWithIcon>
              )}
            </div>
            {data?.price && (
              <Heading className={s.price} level={5}>
                {formatNumberWithSpaces(data.price)}
              </Heading>
            )}
          </div>
        </div>
      </div>
      {data?.image?.url && (
        <Image
          src={data.image.url}
          alt={data.image?.alternativeText ?? "event image"}
          className={s.image}
        />
      )}
    </section>
  );
};

Hero.displayName = "Hero";
